import { useParams } from 'react-router-dom'

import { Box, ColumnSplit, Link } from '@factorialco/deprecated-design-system'

import Loading from 'modules/core/components/Loading'
import NotFound from 'modules/core/components/NotFound'
import PanesHeader from 'modules/core/components/PanesHeader'
import { useAsyncData } from 'modules/core/hooks/useAsyncData'
import { useCurrentSession } from 'modules/core/hooks/useCurrentSession'
import { useNavigation } from 'modules/core/hooks/useNavigation'
import i18n from 'modules/core/lib/i18n'
import { questionGroupRespondent } from 'modules/core/store/collections/users'

import { employeeReferenceContract } from 'modules/contracts/lib/utils'
import type { ContractVersions } from 'modules/contracts/store/collections/contractVersions'
import contractVersions from 'modules/contracts/store/collections/contractVersions'
import hasRegularAccess from 'modules/employees/lib/hasRegularAccess'
import { ResponsivePage } from 'modules/layout/components/ResponsivePage'
import UserBlock from 'modules/surveys/components/UserBlock'
import BlankSlate from 'modules/surveys/components/WriteResponse/BlankSlate'
import ResponseValues from 'modules/surveys/components/WriteResponse/ResponseValues'
import { useForm } from 'modules/surveys/hooks/useForm'
import { useQuestionGroup } from 'modules/surveys/hooks/useQuestionGroup'
import type Form from 'modules/surveys/store/models/Form'
import type QuestionGroup from 'modules/surveys/store/models/QuestionGroup'

interface QuestionsProps {
  questionGroup: QuestionGroup
}

const Questions = ({ questionGroup }: QuestionsProps) => {
  if (!questionGroup.attributes.questions.length) {
    return <BlankSlate />
  }

  return <ResponseValues questionGroup={questionGroup} />
}

interface Props {
  questionGroupId: number
  form: Form
  contractVersions: ContractVersions
}

function Response({ form, questionGroupId, contractVersions }: Props) {
  const { employee: currentEmployee } = useCurrentSession()
  const { id: formId } = useParams()
  const { loading, questionGroup } = useQuestionGroup(questionGroupId)
  const user = questionGroup && questionGroupRespondent(questionGroup)
  const employee = user && user.employee
  const jobTitle = employee
    ? employeeReferenceContract(contractVersions, employee)?.attributes.job_title
    : null
  const canEdit = form && form.policy.can('editSurveysForm')
  const { navigation } = useNavigation()

  const hasWelcomeSpaceAccess =
    !!currentEmployee && !hasRegularAccess(currentEmployee.hasRegularAccess)

  if (loading) return <Loading />

  if (!questionGroup) {
    return <NotFound />
  }

  const back = hasWelcomeSpaceAccess
    ? navigation.employee_onboarding.onboardingTasks.fullPath()
    : canEdit
    ? navigation.engagement.surveys.id.responses.fullPath({ id: String(formId) })
    : navigation.engagement.surveys.fullPath()

  return (
    <Box width="full" flexGrow>
      <ColumnSplit colored={false} minWidth="none">
        <PanesHeader
          illustration="surveys"
          title={form ? form.attributes.name : ''}
          description={i18n.t('surveys.form.response.description')}
          descriptionLink={<Link.Router to={back}>{i18n.t('common.go_back')}</Link.Router>}
        />
        <Box>
          {questionGroup && (
            <UserBlock
              user={!user ? null : user}
              description={jobTitle}
              isAnonymous={form.isAnonymous}
            >
              <Questions questionGroup={questionGroup} />
            </UserBlock>
          )}
        </Box>
      </ColumnSplit>
    </Box>
  )
}

export default function WriteSurveyResponsePage() {
  const { id: formId, question_group_id } = useParams()
  const { loading, form } = useForm(Number(formId))

  const { loading: loadingContractVersions } = useAsyncData(
    () => contractVersions.fetch({ withCachedFetchParams: true }),
    []
  )

  return (
    <ResponsivePage.Body
      loading={loading || loadingContractVersions}
      showHeader={false}
      error={null}
    >
      {form && (
        <Response
          questionGroupId={Number(question_group_id)}
          form={form}
          contractVersions={contractVersions}
        />
      )}
    </ResponsivePage.Body>
  )
}
